"use client";

import { useMemo, useCallback } from "react";
import { useHyperliquidAbstractionMode } from "@cede/trading-data";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { HIP3_PLATFORMS, AIRDROP_FARMING_EXCHANGE_CONFIG } from "@/constants/airdrop-farming";
import type { Hip3PlatformId, AirdropFarmingTabId } from "@/constants/airdrop-farming";
import type { ExchangeConnection } from "@/hooks/airdrop-farming/types";
import { useExchangeAccountBalance } from "@/hooks/airdrop-farming/useExchangeAccountBalance";
import { useHyperliquidFarmingBalance } from "@/hooks/airdrop-farming/useHyperliquidFarmingBalance";

export interface AirdropFarmingBalance {
  // Core — all exchanges
  availableUsd: string;
  settlementCoin: string;
  settlementCoinBalance: string;

  // Swap (HL: real values, others: "0"/false)
  supportsSwap: boolean;
  spotUsdc: string;
  spotUsdcForSwap: string;
  getSpotBalance: (coin: string) => string;

  // Transfer spot->perp (HL: function, others: null)
  transferToPerps: ((amount: string) => Promise<void>) | null;
  isTransferring: boolean;

  // Loading
  /** True while the initial balance fetch is in progress (no data yet). */
  isFetchingBalance: boolean;
  /** True once at least one fetch has completed. */
  hasFetchedBalance: boolean;

  // Actions
  refetchBalances: () => void;
}

interface UseAirdropFarmingBalanceOptions {
  exchangeId: AirdropFarmingTabId;
  selectedHip3Platform: Hip3PlatformId;
  primaryWalletAddress: string | undefined;
  connection: ExchangeConnection | null;
  /** Account ID for non-HL exchanges — needed to fetch balance from backend */
  accountId?: number | null;
  /** Quote coin override */
  quoteCoin?: string;
}

export function useAirdropFarmingBalance({
  exchangeId,
  selectedHip3Platform,
  primaryWalletAddress,
  connection,
  accountId,
  quoteCoin,
}: UseAirdropFarmingBalanceOptions): AirdropFarmingBalance {
  const isHyperliquidBased = exchangeId === SUPPORTED_EXCHANGES.HYPERLIQUID;

  const settlementCoin = isHyperliquidBased
    ? HIP3_PLATFORMS[selectedHip3Platform].settlementCoin
    : (quoteCoin ?? AIRDROP_FARMING_EXCHANGE_CONFIG[exchangeId]?.settlementCoin ?? "USDC");

  // Unified account check — only for HL-based exchanges
  const { usesUnifiedBalanceSource: isUnifiedMode } = useHyperliquidAbstractionMode(
    isHyperliquidBased ? primaryWalletAddress : undefined,
    isHyperliquidBased,
  );

  // Hyperliquid balance logic (delegated to dedicated hook)
  const hlBalance = useHyperliquidFarmingBalance({
    primaryWalletAddress,
    isUnifiedMode,
    settlementCoin,
    enabled: isHyperliquidBased,
  });

  // Backend-provided balance for non-HL exchanges (shared via query cache)
  const backendBalance = useExchangeAccountBalance(isHyperliquidBased ? null : accountId, exchangeId, quoteCoin);

  // Spot -> Perp transfer (HL-based exchanges only)
  const transferToPerps = useMemo(() => {
    if (!isHyperliquidBased || !connection?.transferUsd) return null;
    const { transferUsd } = connection;
    return async (amount: string) => {
      await transferUsd({ amount, toPerp: true });
      hlBalance.fetchBalances();
    };
  }, [isHyperliquidBased, connection, hlBalance]);

  const isTransferring = isHyperliquidBased ? (connection?.isTransferring ?? false) : false;

  const refetchBalances = useCallback(() => {
    if (isHyperliquidBased) {
      hlBalance.fetchBalances();
    } else {
      backendBalance.invalidate();
    }
  }, [isHyperliquidBased, hlBalance, backendBalance]);

  // Route values based on exchange type
  const availableUsd = isHyperliquidBased ? hlBalance.combinedUsdBalance : backendBalance.balance;
  const settlementCoinBalance = isHyperliquidBased ? hlBalance.settlementCoinBalance : backendBalance.balance;

  const isFetchingBalance = isHyperliquidBased ? hlBalance.isFetching : backendBalance.isFetching;
  const hasFetchedBalance = isHyperliquidBased ? hlBalance.hasFetched : backendBalance.hasFetched;

  return {
    availableUsd,
    settlementCoin,
    settlementCoinBalance,

    supportsSwap: isHyperliquidBased,
    spotUsdc: hlBalance.spotUsdc,
    spotUsdcForSwap: hlBalance.spotUsdcForSwap,
    getSpotBalance: hlBalance.getSpotBalance,

    transferToPerps,
    isTransferring,

    isFetchingBalance,
    hasFetchedBalance,

    refetchBalances,
  };
}
